import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";
import type { ApprovalQueueTask } from "serviceStores/mock/approval-queue.mock";
import type { PurchaseOrder } from "serviceStores/mock/purchase-orders.mock";
import type { Plant } from "serviceStores/mock/plants.mock";

/**
 * Receiving Queue Store
 *
 * Manages the receiving queue view - POs pending receipt of goods.
 * Used by receiving staff (Jason's queue).
 */
class ReceivingQueueStore {
  rootStore: RootStore;

  // Filters
  plantFilter: number | null = null;
  vendorFilter: string = "";
  searchTerm: string = "";
  statusFilter: "all" | "pending" | "overdue" = "pending";
  priorityFilter: "all" | "urgent" | "normal" = "all";

  // Sort
  sortField: "dueDate" | "createdDate" | "totalAmount" | "vendorName" =
    "dueDate";
  sortDirection: "asc" | "desc" = "asc";

  // UI State
  loading = false;
  error: string | null = null;
  selectedTaskIds: number[] = [];

  // Reference Data
  plants: Plant[] = [];
  tasks: ApprovalQueueTask[] = [];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ==================== Filtered & Sorted Data ====================

  get filteredTasks(): ApprovalQueueTask[] {
    let filtered = this.tasks.filter((t) => t.queueType === "receiving");

    // Status filter
    if (this.statusFilter === "pending") {
      filtered = filtered.filter((t) => t.status === "pending");
    } else if (this.statusFilter === "overdue") {
      const today = new Date().toISOString().split("T")[0];
      filtered = filtered.filter(
        (t) => t.status === "pending" && t.dueDate && t.dueDate < today
      );
    }

    // Plant filter
    if (this.plantFilter) {
      filtered = filtered.filter((t) => t.plantId === this.plantFilter);
    }

    // Priority filter
    if (this.priorityFilter !== "all") {
      filtered = filtered.filter((t) => t.priority === this.priorityFilter);
    }

    // Vendor filter
    if (this.vendorFilter) {
      filtered = filtered.filter((t) =>
        t.vendorName?.toLowerCase().includes(this.vendorFilter.toLowerCase())
      );
    }

    // Search
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.entityNumber?.toLowerCase().includes(search) ||
          t.vendorName?.toLowerCase().includes(search) ||
          t.title?.toLowerCase().includes(search)
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (this.sortField) {
        case "dueDate":
          aVal = a.dueDate || "9999-99-99";
          bVal = b.dueDate || "9999-99-99";
          break;
        case "createdDate":
          aVal = a.createdDate || "";
          bVal = b.createdDate || "";
          break;
        case "totalAmount":
          aVal = a.totalAmount || 0;
          bVal = b.totalAmount || 0;
          break;
        case "vendorName":
          aVal = a.vendorName || "";
          bVal = b.vendorName || "";
          break;
        default:
          aVal = a.dueDate || "";
          bVal = b.dueDate || "";
      }

      if (this.sortDirection === "asc") {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

    return filtered;
  }

  get tasksByPlant(): Array<{ plant: Plant; count: number; urgent: number }> {
    return this.plants.map((plant) => {
      const plantTasks = this.tasks.filter(
        (t) =>
          t.queueType === "receiving" &&
          t.plantId === plant.plantId &&
          t.status === "pending"
      );
      return {
        plant,
        count: plantTasks.length,
        urgent: plantTasks.filter((t) => t.priority === "urgent").length,
      };
    });
  }

  get totalPendingCount(): number {
    return this.tasks.filter(
      (t) => t.queueType === "receiving" && t.status === "pending"
    ).length;
  }

  get overdueCount(): number {
    const today = new Date().toISOString().split("T")[0];
    return this.tasks.filter(
      (t) =>
        t.queueType === "receiving" &&
        t.status === "pending" &&
        t.dueDate &&
        t.dueDate < today
    ).length;
  }

  get urgentCount(): number {
    return this.tasks.filter(
      (t) =>
        t.queueType === "receiving" &&
        t.status === "pending" &&
        t.priority === "urgent"
    ).length;
  }

  // ==================== Filter Actions ====================

  setPlantFilter = (plantId: number | null) => {
    this.plantFilter = plantId;
  };

  setVendorFilter = (vendor: string) => {
    this.vendorFilter = vendor;
  };

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setStatusFilter = (status: "all" | "pending" | "overdue") => {
    this.statusFilter = status;
  };

  setPriorityFilter = (priority: "all" | "urgent" | "normal") => {
    this.priorityFilter = priority;
  };

  setSort = (
    field: "dueDate" | "createdDate" | "totalAmount" | "vendorName"
  ) => {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortField = field;
      this.sortDirection = "asc";
    }
  };

  clearFilters = () => {
    this.plantFilter = null;
    this.vendorFilter = "";
    this.searchTerm = "";
    this.statusFilter = "pending";
    this.priorityFilter = "all";
  };

  // ==================== Selection ====================

  toggleTaskSelection = (taskId: number) => {
    const index = this.selectedTaskIds.indexOf(taskId);
    if (index >= 0) {
      this.selectedTaskIds.splice(index, 1);
    } else {
      this.selectedTaskIds.push(taskId);
    }
  };

  selectAllVisible = () => {
    this.selectedTaskIds = this.filteredTasks.map((t) => t.taskId);
  };

  clearSelection = () => {
    this.selectedTaskIds = [];
  };

  // ==================== Actions ====================

  openReceivingWizard = (taskId?: number) => {
    if (taskId) {
      // Could pass task ID as query param in future
    }
    this.rootStore.routerStore.goTo("POReceivingWizard");
  };

  // ==================== Data Loading ====================

  loadInitialData = async () => {
    this.loading = true;
    this.error = null;

    try {
      // Load plants
      if ((this.rootStore as any).plantsStore) {
        await (this.rootStore as any).plantsStore.find();
      }

      // Load tasks
      if ((this.rootStore as any).approvalQueueStore) {
        await (this.rootStore as any).approvalQueueStore.find({
          query: { queueType: "receiving" },
        });
      }

      runInAction(() => {
        this.plants = (this.rootStore as any).plantsStore?.entities || [];
        this.tasks = (this.rootStore as any).approvalQueueStore?.entities || [];
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load data";
        this.loading = false;
      });
    }
  };

  reset = () => {
    this.clearFilters();
    this.selectedTaskIds = [];
    this.error = null;
  };
}

export default ReceivingQueueStore;
